import React, { useEffect, useState } from "react";
import { Download, X, Share, SquarePlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/lib/i18n";

const DISMISS_KEY = "pmm_install_banner_dismissed";

export const InstallBanner = () => {
  const { lang } = useI18n();
  const ms = lang === "ms";
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [visible, setVisible] = useState(false);
  const [showIos, setShowIos] = useState(false);

  const isStandalone =
    typeof window !== "undefined" &&
    (window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true);
  const isIos = typeof navigator !== "undefined" && /iphone|ipad|ipod/i.test(navigator.userAgent);

  useEffect(() => {
    if (isStandalone || localStorage.getItem(DISMISS_KEY)) return;
    if (isIos) {
      setVisible(true);
      return;
    }
    const onPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", () => setVisible(false));
    return () => window.removeEventListener("beforeinstallprompt", onPrompt);
  }, [isStandalone, isIos]);

  const install = async () => {
    if (!deferredPrompt) {
      setShowIos(true);
      return;
    }
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === "accepted") setVisible(false);
    setDeferredPrompt(null);
  };

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, "1");
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div className="fixed inset-x-0 bottom-0 z-50 p-3 sm:p-4" data-testid="install-banner">
      <div className="mx-auto max-w-xl overflow-hidden rounded-[1.5rem] border border-yellow-400/30 bg-gradient-to-r from-blue-950 via-blue-900 to-blue-950 shadow-2xl shadow-blue-950/50">
        <div className="h-0.5 w-full bg-gradient-to-r from-transparent via-yellow-400 to-transparent" />
        <div className="flex items-center gap-3 p-4">
          <div className="grid h-11 w-11 shrink-0 place-items-center rounded-2xl bg-gradient-to-br from-orange-600 to-orange-400 text-white shadow-lg shadow-orange-500/40">
            <Download className="h-5 w-5" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="font-display text-sm font-extrabold uppercase tracking-wide text-white">
              {ms ? "Install App PMM" : "Install PMM App"}
            </div>
            <p className="truncate text-xs font-semibold text-blue-200">
              {ms ? "Akses pantas dari skrin utama phone anda" : "Quick access from your phone home screen"}
            </p>
          </div>
          <Button
            onClick={install}
            size="sm"
            className="shrink-0 rounded-full bg-gradient-to-r from-orange-600 to-orange-500 px-4 font-display text-xs font-extrabold uppercase tracking-wide text-white shadow-lg shadow-orange-500/40 transition-transform duration-300 hover:scale-105"
            data-testid="install-banner-btn"
          >
            Install
          </Button>
          <button
            onClick={dismiss}
            className="shrink-0 rounded-full p-1.5 text-blue-300 transition-colors hover:bg-white/10 hover:text-white"
            aria-label={ms ? "Tutup" : "Close"}
            data-testid="install-banner-close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        {showIos && (
          <div className="border-t border-white/10 px-4 py-3 text-xs font-semibold text-blue-100" data-testid="install-ios-steps">
            {ms ? (
              <span className="inline-flex flex-wrap items-center gap-1">
                Di Safari: tekan <Share className="h-3.5 w-3.5 text-yellow-400" /> <b className="text-yellow-400">Share</b> kemudian pilih
                <SquarePlus className="h-3.5 w-3.5 text-yellow-400" /> <b className="text-yellow-400">Add to Home Screen</b>
              </span>
            ) : (
              <span className="inline-flex flex-wrap items-center gap-1">
                In Safari: tap <Share className="h-3.5 w-3.5 text-yellow-400" /> <b className="text-yellow-400">Share</b> then choose
                <SquarePlus className="h-3.5 w-3.5 text-yellow-400" /> <b className="text-yellow-400">Add to Home Screen</b>
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default InstallBanner;
